/**
 * The AI policy writer (Phase 4C) on the firm's Policies page. The IT lead tells the writer what the firm
 * does; it asks follow-up questions and drafts the policy. The draft opens for review: clauses written for
 * the firm are highlighted and editable, the rest keep the National wording, and the portal's clause checks
 * run as the IT lead types. Saving re-runs every check on the server, then downloads the Word document.
 */
import {
  applyTailoring,
  canTailor,
  clauseNumbers,
  fillText,
  MAX_PRACTICE_ANSWER,
  policyTemplate,
  PRACTICE_QUESTIONS_STATUS,
  practiceMessage,
  practiceQuestionsFor,
  tailoringProblems,
  type Answers,
  type PolicyTemplate,
  type PracticeAnswers,
  type PracticeQuestion,
  type TailoringProblem,
} from '@grc/shared/policies';
import { useMemo, useState, type FormEvent } from 'react';
import { ApiError, useApi, useApiClient, useDownload } from '../api';
import { showDraftLabel } from '../config';
import { ChatLog, RichText } from './AssistantPanel';
import { PlanProposal, type ProposedPlan } from './PlanProposal';

export interface WriterQuestion {
  key: string;
  label: string;
  help?: string;
}

export interface WriterDraft {
  templateId: string;
  answers: Record<string, string>;
  tailoring: { clauses: Record<string, string>; omitted: string[] };
  openQuestions: string[];
  summary: string;
  corrections: string[];
  promptInjectionNoticed: boolean;
}

interface WriterMessage {
  id: number;
  role: 'user' | 'assistant';
  kind: 'chat' | 'draft';
  content: string | WriterDraft;
}

interface WriterState {
  enabled: boolean;
  conversation: { sessionId: string; messages: WriterMessage[] } | null;
}

const PLACEHOLDER = /\[[^\]]{2,}\]/;

/** Splits "Password manager: does the rollout start this year?" into a short label and the rest. */
function splitFinding(item: string): { label: string | null; text: string } {
  const i = item.indexOf(':');
  if (i >= 0) {
    const label = item.slice(0, i).trim();
    const text = item.slice(i + 1).trim();
    if (label.length >= 3 && label.length <= 80 && text) return { label, text };
  }
  return { label: null, text: item.trim() };
}

/** A starting action plan for an item still to confirm, built from the item itself (no AI call). */
function planFromFinding(item: string, template: PolicyTemplate, title: string): ProposedPlan {
  const { label, text } = splitFinding(item);
  return {
    theme: label ?? title,
    objective: (text.charAt(0).toUpperCase() + text.slice(1)).slice(0, 600),
    controls: template.controls.slice(0, 6),
    priority: 'medium',
    why: `From the ${title} review.`,
  };
}

/**
 * The policy's own questions about the firm's practice (DEC-49), each answer optional. Used on the short form and
 * at the start of the writer's interview; the writer receives the answers as the conversation's opening message.
 */
export function PracticeFields({ questions, answers, onChange, idPrefix, disabled = false }: {
  questions: PracticeQuestion[];
  answers: PracticeAnswers;
  onChange: (next: PracticeAnswers) => void;
  idPrefix: string;
  disabled?: boolean;
}) {
  return (
    <div className="practice-fields">
      {questions.map((q) => (
        <label key={q.id} className="practice-field">
          <span className="q-label">{q.ask}</span>
          <textarea
            rows={2}
            maxLength={MAX_PRACTICE_ANSWER}
            value={answers[q.id] ?? ''}
            disabled={disabled}
            onChange={(e) => onChange({ ...answers, [q.id]: e.target.value })}
            aria-describedby={q.hint ? `${idPrefix}-${q.id}` : undefined}
          />
          {q.hint && (
            <span id={`${idPrefix}-${q.id}`} className="small practice-hint">
              {q.hint}
            </span>
          )}
        </label>
      ))}
    </div>
  );
}

/** Answers with something in them, trimmed, as the API takes them. */
export const filledPractice = (a: PracticeAnswers): PracticeAnswers => Object.fromEntries(Object.entries(a).map(([k, v]) => [k, v.trim()]).filter(([, v]) => v));

export function PolicyWriter({ firmCode, firmName, title, templateId, questions, defaults, practice, onPractice, onCreated }: {
  firmCode: string;
  firmName: string;
  title: string;
  templateId: string;
  questions: WriterQuestion[];
  defaults: Record<string, string>;
  /** Answers to the policy's own questions so far (from the short form, or typed here). */
  practice: PracticeAnswers;
  onPractice: (next: PracticeAnswers) => void;
  onCreated: () => void;
}) {
  const state = useApi<WriterState>(`/firms/${firmCode}/policies/writer?templateId=${encodeURIComponent(templateId)}`);
  const template = policyTemplate(templateId);
  if (!template) return null;
  if (state.loading) return <p className="muted">Opening the policy writer…</p>;
  if (state.error) return <p role="alert" className="notice">{state.error.message}</p>;
  if (!state.data?.enabled) return <p className="notice">The policy writer is not switched on in this environment yet. Use "Answer a few questions" instead.</p>;
  return (
    <Writer
      firmCode={firmCode}
      firmName={firmName}
      title={title}
      template={template}
      questions={questions}
      defaults={defaults}
      practice={practice}
      onPractice={onPractice}
      initial={state.data.conversation}
      onCreated={onCreated}
    />
  );
}

function Writer({ firmCode, firmName, title, template, questions, defaults, practice, onPractice, initial, onCreated }: {
  firmCode: string;
  firmName: string;
  title: string;
  template: PolicyTemplate;
  questions: WriterQuestion[];
  defaults: Record<string, string>;
  practice: PracticeAnswers;
  onPractice: (next: PracticeAnswers) => void;
  initial: WriterState['conversation'];
  onCreated: () => void;
}) {
  const call = useApiClient();
  const [sessionId, setSessionId] = useState<string | null>(initial?.sessionId ?? null);
  const [messages, setMessages] = useState<WriterMessage[]>(initial?.messages ?? []);
  const [message, setMessage] = useState('');
  const [busy, setBusy] = useState<null | 'send' | 'draft'>(null);
  const [error, setError] = useState<string | null>(null);
  const lastDraft = [...messages].reverse().find((m) => m.kind === 'draft');
  const [review, setReview] = useState<{ id: number; draft: WriterDraft } | null>(null);
  const chat = messages.filter((m) => m.kind === 'chat');
  const base = `/firms/${firmCode}/policies/writer`;

  const guard = async (what: typeof busy, fn: () => Promise<void>) => {
    setBusy(what);
    setError(null);
    try {
      await fn();
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'Something went wrong. Please try again.');
    } finally {
      setBusy(null);
    }
  };

  // The policy's own questions (DEC-49) open the interview: the server turns the answers into the IT lead's
  // message with the same shared function, so what shows here is what the writer reads and the transcript keeps.
  const practiceQs = practiceQuestionsFor(template);
  const answered = filledPractice(practice);
  const answeredKey = JSON.stringify(answered);
  // The answers last sent to the writer, so changed answers can be sent again once a conversation exists (CP-02).
  const [sentKey, setSentKey] = useState<string | null>(null);
  const unsent = Object.keys(answered).length > 0 && answeredKey !== sentKey;

  /**
   * One turn. Answers not yet sent go with it (UX-01, CP-03: Send and "Draft the policy" carried only the typed
   * message, and answers given before the first message were lost). Returns the conversation's id.
   */
  const turn = async (note: string, withAnswers: boolean): Promise<string> => {
    const r = await call<{ sessionId: string; messageId: number; reply: string }>(`${base}/turn`, {
      method: 'POST',
      body: { templateId: template.id, sessionId, ...(withAnswers ? { practice: answered } : {}), ...(note ? { message: note } : {}) },
    });
    setSessionId(r.sessionId);
    const said = withAnswers ? practiceMessage(template, answered, note || undefined) : note;
    setMessages((m) => [...m, { id: -r.messageId, role: 'user', kind: 'chat', content: said }, { id: r.messageId, role: 'assistant', kind: 'chat', content: r.reply }]);
    if (withAnswers) setSentKey(answeredKey);
    setMessage('');
    return r.sessionId;
  };

  const send = (e: FormEvent) => {
    e.preventDefault();
    const text = message.trim();
    if (!text) return;
    return guard('send', async () => void (await turn(text, chat.length === 0 && unsent)));
  };

  const start = (e: FormEvent) => {
    e.preventDefault();
    if (!Object.keys(answered).length) return;
    return guard('send', async () => void (await turn(message.trim(), true)));
  };

  const makeDraft = () =>
    guard('draft', async () => {
      // Answers given before any message open the conversation first, so the draft reads them.
      const sid = chat.length === 0 && unsent ? await turn(message.trim(), true) : sessionId;
      const r = await call<{ sessionId: string; draftMessageId: number; draft: WriterDraft }>(`${base}/draft`, { method: 'POST', body: { templateId: template.id, sessionId: sid } });
      setSessionId(r.sessionId);
      setMessages((m) => [...m, { id: r.draftMessageId, role: 'assistant', kind: 'draft', content: r.draft }]);
      setReview({ id: r.draftMessageId, draft: r.draft });
    });

  if (review) {
    return (
      <DraftReview
        firmCode={firmCode}
        firmName={firmName}
        title={title}
        template={template}
        questions={questions}
        defaults={defaults}
        draftId={review.id}
        draft={review.draft}
        onBack={() => setReview(null)}
        onCreated={onCreated}
      />
    );
  }

  return (
    <section className="assistant-side writer-panel" aria-labelledby="writer-h">
      <h3 id="writer-h">Policy writer</h3>
      <p className="assistant-intro">
        Tell the writer, in your own words, what {firmName} does today for this policy. It asks a question or two at a time, explains anything unclear, and then drafts
        the policy for you to review. It uses your firm's own portal records and National guidance, and never another firm's.
      </p>
      <p className="assistant-note">It covers:</p>
      <ul className="plain chip-list writer-topics" aria-label="What the policy covers">
        {template.sections.map((s) => (
          <li key={s.heading} className="chip">
            {s.heading}
          </li>
        ))}
      </ul>
      {chat.length === 0 && practiceQs.length > 0 && (
        <form className="form practice-start" aria-label="This policy's questions" onSubmit={start}>
          <h4>Start with this policy's questions</h4>
          <p className="assistant-note">
            Answer what you can, in your own words, and leave the rest blank: the writer uses your answers to adapt the policy and asks about anything left open.
          </p>
          {showDraftLabel(PRACTICE_QUESTIONS_STATUS) && <p className="assistant-note small">These questions are a draft for National IT review.</p>}
          <PracticeFields questions={practiceQs} answers={practice} onChange={onPractice} idPrefix="wq" disabled={busy !== null} />
          <div className="actions">
            <button className="pill" disabled={busy !== null || !Object.keys(answered).length}>
              {busy === 'send' ? 'Starting…' : 'Start with these answers'}
            </button>
          </div>
          <p className="assistant-note small">Or write to the writer in your own words below.</p>
        </form>
      )}
      <ChatLog count={chat.length}>
        {chat.length === 0 && <li className="assistant-note">Start with the tools you use, who looks after them, and anything that is not in place yet.</li>}
        {chat.map((m) => (
          <li key={m.id} className={`bubble ${m.role}`}>
            <span className="who">{m.role === 'user' ? 'You' : 'Policy writer'}</span>
            {m.role === 'assistant' ? <RichText text={m.content as string} /> : <p className="pre">{m.content as string}</p>}
          </li>
        ))}
        {busy && (
          <li className="bubble assistant" role="status">
            <span className="who">Policy writer</span>
            <p>{busy === 'draft' ? 'Drafting your policy and checking every required clause…' : 'Reading your records and the template…'}</p>
          </li>
        )}
      </ChatLog>
      {chat.length > 0 && unsent && (
        <form className="form practice-update" aria-label="Your answers to this policy's questions" onSubmit={start}>
          <p className="assistant-note">
            You have answered {Object.keys(answered).length} of this policy's {practiceQs.length} questions, and the writer has not read these answers yet. Send them as
            your next message so it uses them.
          </p>
          <div className="actions">
            <button className="pill secondary-dark" disabled={busy !== null}>
              {busy === 'send' ? 'Sending…' : 'Send your answers to the writer'}
            </button>
          </div>
        </form>
      )}
      <form onSubmit={send} className="form">
        <label>
          Your message
          <textarea rows={4} maxLength={8000} value={message} onChange={(e) => setMessage(e.target.value)} disabled={busy !== null} />
        </label>
        <div className="actions">
          <button className="pill" disabled={busy !== null || !message.trim()}>
            {busy === 'send' ? 'Thinking…' : 'Send'}
          </button>
          <button type="button" className="pill secondary-dark" disabled={busy !== null} onClick={makeDraft}>
            {busy === 'draft' ? 'Drafting…' : 'Draft the policy'}
          </button>
          {lastDraft && (
            <button type="button" className="link" onClick={() => setReview({ id: lastDraft.id, draft: lastDraft.content as WriterDraft })}>
              Review the last draft
            </button>
          )}
        </div>
      </form>
      {error && (
        <p role="alert" className="assistant-error">
          {error}
        </p>
      )}
    </section>
  );
}

function DraftReview({ firmCode, firmName, title, template, questions, defaults, draftId, draft, onBack, onCreated }: {
  firmCode: string;
  firmName: string;
  title: string;
  template: PolicyTemplate;
  questions: WriterQuestion[];
  defaults: Record<string, string>;
  draftId: number;
  draft: WriterDraft;
  onBack: () => void;
  onCreated: () => void;
}) {
  const call = useApiClient();
  const download = useDownload();
  const [answers, setAnswers] = useState<Record<string, string>>(() => ({ ...defaults, ...draft.answers }));
  const [clauses, setClauses] = useState<Record<string, string>>(() => ({ ...draft.tailoring.clauses }));
  const [omitted, setOmitted] = useState<string[]>(() => [...draft.tailoring.omitted]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Which "still to confirm" item has its action plan proposal open (one at a time).
  const [tracking, setTracking] = useState<number | null>(null);
  // Items already added as plans stay marked, so the same gap is not added twice.
  const [tracked, setTracked] = useState<Record<number, string>>({});
  const numbers = useMemo(() => clauseNumbers(template), [template]);

  // The same checks the server runs on save, as the IT lead types.
  const problems: TailoringProblem[] = useMemo(() => tailoringProblems(template, answers as Answers, { clauses, omitted }), [template, answers, clauses, omitted]);
  const byClause = (id: string) => problems.filter((p) => p.clauseId === id);
  const general = problems.filter((p) => p.clauseId === null);
  const filled = useMemo(() => applyTailoring(template, answers as Answers, { clauses, omitted }), [template, answers, clauses, omitted]);
  const toFill = filled.sections.flatMap((s) => s.clauses).filter((c) => PLACEHOLDER.test(c.text)).length;
  const tailoredCount = Object.keys(clauses).length;

  const save = async (e: FormEvent) => {
    e.preventDefault();
    if (problems.length) return;
    setBusy(true);
    setError(null);
    try {
      const r = await call<{ id: number; versionNo: number }>(`/firms/${firmCode}/policies`, {
        method: 'POST',
        body: { templateId: template.id, answers, tailoring: { clauses, omitted }, writerDraftMessageId: draftId },
      });
      await download(`/firms/${firmCode}/policies/${r.id}/document`, `${firmName} ${title} v${r.versionNo}.docx`);
      onCreated();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'The policy was not saved.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <section className="draft policy-review" aria-labelledby="review-h">
      <h3 id="review-h">Review your policy</h3>
      <p className="notice small">
        Drafted by the policy writer. Highlighted clauses were written for {firmName}; the others keep the National wording. Check each one, and change anything that is
        not right, before you save.
      </p>
      {draft.promptInjectionNoticed && <p className="notice small">The writer noticed a message that tried to change its instructions. It ignored it.</p>}
      <ul className="plain review-stats" aria-label="What the draft contains">
        <li>
          <strong>{tailoredCount}</strong> written for your firm
        </li>
        <li>
          <strong>{omitted.length}</strong> optional left out
        </li>
        <li>
          <strong>{draft.openQuestions.length + toFill}</strong> still to confirm
        </li>
      </ul>
      {(draft.summary || draft.openQuestions.length > 0 || toFill > 0) && (
        <section className="findings" aria-labelledby="findings-h">
          <h4 id="findings-h">What the writer found</h4>
          {draft.summary && <p className="findings-summary">{draft.summary}</p>}
          {(draft.openQuestions.length > 0 || toFill > 0) && (
            <>
              <h5>Still to confirm</h5>
              <p className="muted small">Confirm each one before you save, or track it as an action plan so it is not lost.</p>
              <ul className="plain findings-list">
                {draft.openQuestions.map((q, i) => {
                  const { label, text } = splitFinding(q);
                  return (
                    <li key={q} className="finding">
                      <div className="finding-row">
                        <p className="finding-text">
                          {label && <strong>{label}: </strong>}
                          {text}
                        </p>
                        {tracked[i] ? (
                          <span className="small finding-tracked" role="status">
                            Tracked as {tracked[i]}
                          </span>
                        ) : (
                          tracking !== i && (
                            <button type="button" className="link finding-track" onClick={() => setTracking(i)}>
                              Track as an action plan<span className="sr-only"> for {label ?? text}</span>
                            </button>
                          )
                        )}
                      </div>
                      {tracking === i && !tracked[i] && (
                        <PlanProposal
                          firmCode={firmCode}
                          plan={planFromFinding(q, template, title)}
                          canAdd
                          onDone={() => setTracking(null)}
                          onAdded={(code) => (setTracked((t) => ({ ...t, [i]: code })), setTracking(null))}
                        />
                      )}
                    </li>
                  );
                })}
                {toFill > 0 && (
                  <li className="finding">
                    <p className="finding-text">{toFill === 1 ? 'One clause has' : `${toFill} clauses have`} a detail in [square brackets] to fill in.</p>
                  </li>
                )}
              </ul>
            </>
          )}
        </section>
      )}
      {draft.corrections.length > 0 && <p className="muted small">The portal corrected the writer's wording to the Baker Tilly writing standards (long dashes, spelling or emoji).</p>}

      <form className="form" onSubmit={save} aria-label={`Save the ${title}`}>
        <p className="muted small review-legend">
          <span className="legend-swatch" aria-hidden="true" /> Written for your firm. Required clauses keep every number and obligation word from the National wording.
        </p>
        {template.sections.map((s, i) => (
          <div key={s.heading} className="review-section">
            <h4>
              {i + 1}. {s.heading}
            </h4>
            <ol className="plain clauses">
              {s.clauses.map((c) => {
                const n = numbers.get(c.id)!;
                const isOmitted = omitted.includes(c.id) && !c.required;
                const isTailored = Object.hasOwn(clauses, c.id);
                const issues = byClause(c.id);
                return (
                  <li key={c.id} className={`clause${isTailored ? ' tailored' : ''}${isOmitted ? ' omitted' : ''}`}>
                    <div className="clause-head">
                      <strong>{n}</strong>
                      <span className="chip tone-neutral">{c.required ? 'Required' : 'Optional'}</span>
                      {isTailored && <span className="chip tone-reviewed">Written for your firm</span>}
                    </div>
                    {isOmitted ? (
                      <p className="muted small">Left out: this optional clause does not apply to {firmName}.</p>
                    ) : isTailored ? (
                      <label>
                        <span className="sr-only">Clause {n}, written for your firm</span>
                        <textarea
                          rows={4}
                          maxLength={2000}
                          value={clauses[c.id]}
                          onChange={(e) => setClauses({ ...clauses, [c.id]: e.target.value })}
                          aria-invalid={issues.length > 0}
                          aria-describedby={issues.length ? `issue-${c.id}` : undefined}
                        />
                      </label>
                    ) : (
                      <p>{fillText(c.text, answers as Answers)}</p>
                    )}
                    {issues.length > 0 && (
                      <p id={`issue-${c.id}`} className="clause-issue">
                        {issues.map((p) => p.message).join(' ')}
                      </p>
                    )}
                    <div className="actions">
                      {isTailored && (
                        <button
                          type="button"
                          className="link"
                          onClick={() => {
                            const next = { ...clauses };
                            delete next[c.id];
                            setClauses(next);
                          }}
                        >
                          Use the National wording<span className="sr-only"> for clause {n}</span>
                        </button>
                      )}
                      {!isTailored && !isOmitted && canTailor(c) && (
                        <button type="button" className="link" onClick={() => setClauses({ ...clauses, [c.id]: fillText(c.text, answers as Answers) })}>
                          Adapt the wording<span className="sr-only"> of clause {n}</span>
                        </button>
                      )}
                      {!c.required && !isOmitted && (
                        <button type="button" className="link" onClick={() => setOmitted([...omitted, c.id])}>
                          Leave it out<span className="sr-only">: clause {n}</span>
                        </button>
                      )}
                      {isOmitted && (
                        <button type="button" className="link" onClick={() => setOmitted(omitted.filter((x) => x !== c.id))}>
                          Put it back<span className="sr-only">: clause {n}</span>
                        </button>
                      )}
                    </div>
                  </li>
                );
              })}
            </ol>
          </div>
        ))}

        {general.length > 0 && (
          <p className="clause-issue">
            {general.map((p) => p.message).join(' ')}
          </p>
        )}

        <div className="review-details">
          <h4>Before you save: policy details</h4>
          <p className="muted small">These fill in the names, roles and dates used in the policy.</p>
          <div className="q-grid">
            {questions.map((q) => (
              <label key={q.key} className="q-field">
                <span className="q-label">{q.label}</span>
                <input
                  type={q.key === 'effectiveDate' ? 'date' : 'text'}
                  required
                  maxLength={200}
                  value={answers[q.key] ?? ''}
                  onChange={(e) => setAnswers({ ...answers, [q.key]: e.target.value })}
                  aria-describedby={q.help ? `wh-${q.key}` : undefined}
                />
                {q.help && (
                  <span id={`wh-${q.key}`} className="muted small">
                    {q.help}
                  </span>
                )}
              </label>
            ))}
          </div>
        </div>
        <div className="actions">
          <button className="pill" disabled={busy || problems.length > 0}>
            {busy ? 'Saving…' : 'Save and download'}
          </button>
          <button type="button" className="pill secondary-dark" onClick={onBack} disabled={busy}>
            Back to the conversation
          </button>
        </div>
        {problems.length > 0 && <p className="muted small">Fix the {problems.length === 1 ? 'clause' : `${problems.length} clause problems`} marked above to save.</p>}
        <p className="muted small">Saving creates a draft in your firm's policies and opens it in Word on the Baker Tilly letterhead. Saved wording is fixed; a change is a new version.</p>
      </form>
      {error && (
        <p role="alert" className="notice">
          {error}
        </p>
      )}
    </section>
  );
}
